import { useAuth } from '@frontegg/react';
import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import useSWR from 'swr';
import useFetcher from '../utils/hooks/useFetcher';

export interface Language {
    id: number;
    code: string;
    flagImage?: string;
    label: string;
    surveysLanguagesIds: Array<{
        surveyId: number;
        languageId: number;
        isDefault: boolean;
    }>;
}

export interface LanguageContextType {
    selectedLanguage: Language;
    availableLanguages: Language[];
    setSelectedLanguage: (language: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }

    return context;
};

interface LanguageProviderProps {
    children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
    const { user } = useAuth();
    const tenant = user.metadata && (JSON.parse(user.metadata.replace(/,$/, '')).database_schema ? JSON.parse(user.metadata.replace(/,$/, '')).database_schema : 'askia');
    const fetcher = useFetcher();
    const { data: tenantLanguages } = useSWR(`${process.env.REACT_APP_API_URL}/tenant/${tenant}/translations`, fetcher);

    // Initialize with the default language
    const [selectedLanguage, setSelectedLanguage] = useState<Language>(
        tenantLanguages && tenantLanguages[0],
    );

    useEffect(() => {
        if (tenantLanguages && tenantLanguages.length > 0 && !selectedLanguage) {
            setSelectedLanguage(tenantLanguages[0]);
        }
    }, [tenantLanguages, selectedLanguage]);

    const value: LanguageContextType = {
        selectedLanguage,
        availableLanguages: tenantLanguages,
        setSelectedLanguage,
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};
